import { Box, Stack, styled } from '@mui/material';

import ActionButton from '@/components/ActionButton/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { useIsAfterDate } from '@/hooks/useIsAfterDate.ts';
import type { CompetitionWinner } from '@/lib/api/requests.ts';

import { MARCH_COMPETITION_END_DATE } from '../../../../../../config.ts';
import CircleWithNumber from '../CircleWithNumber/index.ts';

const ButtonComponent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.common.white,
  border: '1px solid #18A0FB',
  borderRadius: '8px',
  width: '107%',
  textAlign: 'center',
  padding: '10px',
  zIndex: 1,
}));

const TitleContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  paddingBottom: theme.spacing(1),
}));

const LoginButton = styled(ActionButton)(({ theme }) => ({
  minWidth: 320,
  maxWidth: 320,
  maxHeight: 60,
  fontSize: '19px',
  width: '100%',
  backgroundColor: '#FA8331',
  borderColor: '#FA8331',
  color: '#fff',
  '&:hover': {
    borderColor: '#FA8331',
    color: '#FA8331',
  },
  [theme.breakpoints.up(395)]: {
    minWidth: 370,
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: 450,
    maxHeight: 70,
  },
  [theme.breakpoints.up('md')]: {
    minWidth: 400,
  },
}));

const CircleWithNumberWrapper = styled('div')(({ theme }) => ({
  width: 28,
  height: 28,
  marginLeft: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0),
  },
}));

const Title = styled('p')(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 700,
  fontSize: '27px',
  paddingBottom: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '19px',
    width: '100%',
    paddingBottom: theme.spacing(0),
  },
}));

const Subtitle = styled('p')(({ theme }) => ({
  textAlign: 'center',
  fontSize: '22px',
  width: '80%',
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '19px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    width: '100%',
  },
}));

const SubtitleContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

const WinnerCard = styled('div')(({ theme }) => ({
  backgroundColor: '#F0F4FA',
  padding: theme.spacing(1, 2),
}));

const CTAComponent = (props: { winner: CompetitionWinner | undefined }) => {
  const hasEnded = useIsAfterDate(MARCH_COMPETITION_END_DATE);
  const winner = props.winner;

  return (
    <ButtonComponent>
      <TitleContainer>
        {hasEnded ? (
          <Stack
            sx={{
              mt: 4,
            }}
          >
            <h1 className="mb-2 max-w-[30ch] typography-v1-h3">
              Vinn inntil <UnderlinedText>300 000 kr</UnderlinedText> til nedbetaling av forbruks-
              og kredittkortgjeld
            </h1>
            <p className="mb-1 typography-v1-body3">Denne trekningen er avsluttet</p>
            {winner && (
              <WinnerCard className="typography-v1-h4">
                Vinner: {winner.name} - {winner.age} - {winner.city}
              </WinnerCard>
            )}
          </Stack>
        ) : (
          <>
            <Title className="typography-v1-legacy-p1">Bruk regel</Title>
            <CircleWithNumberWrapper>
              <CircleWithNumber number={3} />
            </CircleWithNumberWrapper>
          </>
        )}
      </TitleContainer>
      <SubtitleContainer>
        {!hasEnded && (
          <Subtitle className="typography-v1-legacy-p1">
            og delta om å vinne inntil{' '}
            <UnderlinedText>
              <b>300.000 kr</b>
            </UnderlinedText>
            <br /> til nedbetaling av din gjeld
          </Subtitle>
        )}
      </SubtitleContainer>
      {hasEnded ? (
        <Box sx={{ mb: 2 }}>
          <LoginButton
            label="Søk om lavere rente"
            step="skip_start"
            flow="debt_check"
            color="secondary"
            gaEvent="march_competition_cta_clicked"
            labelClassName="login-button-text"
            sx={{
              '& .login-button-text': {
                width: '100%',
                fontSize: {
                  xs: '19px',
                  md: '22px',
                },
              },
            }}
          />
        </Box>
      ) : (
        <LoginButton
          label="Sjekk min gjeld og delta"
          step="init"
          flow="march_2024_campaign"
          color="secondary"
          gaEvent="march_competition_cta_clicked"
          labelClassName="login-button-text"
          sx={{
            '& .login-button-text': {
              width: '100%',
              fontSize: {
                xs: '19px',
                md: '22px',
              },
            },
          }}
        />
      )}
    </ButtonComponent>
  );
};

export default CTAComponent;
